/**
 * Copyright (C) 2019 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

export const LuiSuggestionsMenuMixin = Superclass => class extends Superclass {
  get useSuggestions() {
    return this.hasAttribute('suggestions')
  }

  get suggestionsMenuWrapper() {
    this._uiWrapper = this._uiWrapper || this.shadowRoot.querySelector('.lui-suggestions__menu-wrapper')
    if (!this._uiWrapper) {
      throw new Error(`The element '.lui-suggestions__menu-wrapper' was not set! Please set a suggestions wrapper for your class ${this.constructor.name}`)
    }
    return this._uiWrapper
  }

  get searchText() {
    return this.searchTextInput ? this.searchTextInput.value : null
  }

  get searchTextInput() {
    return this.field
  }

  get suggestionsTargetInput() {
    return this.field
  }

  get field() {
    return super.field
  }

  get options() {
    this._options = this._options || this._getOptions()
    return this._options.filter(this._suggestionsOptionsFilter.bind(this))
  }

  createMenu() {
    const menuElement = document.createElement('lui-menu')
    menuElement.id = 'menu'

    this._createMenuOptions(menuElement, this.options)

    const placeholder = this.suggestionsMenuWrapper.querySelector('.lui-suggestions__menu-placeholder')
    this.suggestionsMenuWrapper.insertBefore(menuElement, placeholder)

    return menuElement
  }

  uiMenuClose() {
    this.uiMenu.removeAttribute('open')
    this.uiMenu.style.display = 'none'
    this._toggleHostClass(this.uiMenu.open)
  }

  uiMenuOpen() {
    if (!this.uiMenu.open) {
      this.uiMenu.setAttribute('open', '')
      this._toggleHostClass(true)
    }
  }

  _clearMenuOptions(menuElement) {
    while (menuElement.firstChild) {
      menuElement.removeChild(menuElement.firstChild)
    }
    return menuElement
  }

  _createMenuOptions(menuElement, options) {
    options.forEach(option => {
      const menuItemElement = document.createElement('lui-menuitem')
      menuItemElement.innerHTML = option.label
      menuItemElement.value = option.value
      if (option.selected) {
        menuItemElement.setAttribute('selected', 'selected')
      }
      if (this.searchText) {
        menuItemElement.setAttribute('highlight', this.searchText)
      }
      menuElement.appendChild(menuItemElement)
    })
    return menuElement
  }

  _findOptionByValue(searchValue) {
    return this.options.find(({ value }) => value === String(searchValue))
  }

  _getFilteredOptions(searchText) {
    const normalizedSearchText = (searchText || '').trim().toLowerCase()
    if (!normalizedSearchText) {
      return [...this.options]
    }
    return this.options.filter(this._searchTextFilter.bind(this, normalizedSearchText))
  }

  _getOptions() {
    const optionElements = this.querySelectorAll('datalist > option')

    return Array.from(optionElements).map((optionElement) => ({
      label: optionElement.innerHTML,
      shortLabel: this._getShortLabel(optionElement),
      textContent: this._normalizeTextContent(optionElement.textContent),
      value: optionElement.value,
      selected: optionElement.selected,
    }))
  }

  _getShortLabel(element) {
    const label = element.querySelector('.label')
    return label ? label.textContent : element.textContent
  }

  _initSuggestions() {
    this.field.removeAttribute('list')
  }

  _normalizeTextContent(text) {
    return (text || '').replace(/\s\s+/g, ' ').trim()
  }

  _onBlurSuggestions() {
    const currentOption = this._findOptionByValue(this.searchTextInput.value)

    this._updateTargetInput(currentOption ? currentOption.shortLabel : null)
    this.uiMenuClose()
  }

  _onClickElsewhereSuggestions(element, event) {
    const parent = event.target.closest(element)

    if ((!parent || parent !== this) && this.uiMenu.open) {
      this.uiMenuClose()
    }
  }

  _onFocusSuggestions() {
    if (this.useSuggestions) {
      this._suggestionsUpdate()
      this.uiMenuOpen()
    }
  }

  _onSearchTextInputSuggestions() {
    this._updateUiMenu(this._getFilteredOptions(this.searchText))
    this.dispatchEvent(new Event('lui-suggestions:searchChange', { bubbles: true }))
  }

  _onSuggestionsMenuChange(event) {
    const hasValue = event && event.shadowTarget && event.shadowTarget.value !== undefined
    if (hasValue) {
      const value = event.shadowTarget.value
      if (this.value !== value) {
        this.value = value
        this.field.dispatchEvent(new Event('input'))
        this.dispatchEvent(new Event('change', { bubbles: true }))
      }
      this._updateTargetInput(event.shadowTarget.textContent)
      this._updateUiMenu(this._getFilteredOptions(this.searchText))
      this.uiMenuClose()
    }
  }

  _refreshOptions() {
    this._options = this._getOptions()
  }

  _registerSuggestionsEvents() {
    this._onFocusSuggestionsBound = this._onFocusSuggestionsBound || this._onFocusSuggestions.bind(this)
    this._onBlurSuggestionsBound = this._onBlurSuggestionsBound || this._onBlurSuggestions.bind(this)
    this._onClickElsewhereSuggestionsBound = this._onClickElsewhereSuggestionsBound || this._onClickElsewhereSuggestions.bind(this, this.constructor.is)
    this._onSuggestionsMenuChangeBound = this._onSuggestionsMenuChangeBound || this._onSuggestionsMenuChange.bind(this)
    this._onSearchTextInputSuggestionsBound = this._onSearchTextInputSuggestionsBound || this._onSearchTextInputSuggestions.bind(this)

    this.addEventListener('blur', this._onBlurSuggestionsBound)
    this.uiMenu.addEventListener('change', this._onSuggestionsMenuChangeBound)
    this.searchTextInput.addEventListener('focus', this._onFocusSuggestionsBound)
    this.searchTextInput.addEventListener('input', this._onSearchTextInputSuggestionsBound)
    document.addEventListener('click', this._onClickElsewhereSuggestionsBound)
  }

  _searchTextFilter(searchText, item) {
    return item.textContent.toLowerCase().includes(searchText)
  }

  _suggestionsOptionsFilter() {
    return true
  }

  _suggestionsUpdate() {
    this._refreshOptions()
    this._updateUiMenu(this._getFilteredOptions(this.searchText))
  }

  _toggleHostClass(isOpen) {
    this.classList.toggle('lui-suggestions_menu--open', isOpen)
  }

  _unregisterSuggestionsEvents() {
    this._onBlurSuggestionsBound && this.removeEventListener('blur', this._onBlurSuggestionsBound)
    this._onClickElsewhereSuggestionsBound && document.removeEventListener('click', this._onClickElsewhereSuggestionsBound)
    this._onFocusSuggestionsBound && this.searchTextInput._onFocusSuggestionsBound && this.removeEventListener('focus', this._onFocusSuggestionsBound)
    this._onSearchTextInputSuggestionsBound && this.searchTextInput.removeEventListener('input', this._onSearchTextInputSuggestionsBound)
  }

  _updateTargetInput(value) {
    if (value !== null) {
      this.suggestionsTargetInput.value = this._normalizeTextContent(value)
    }
  }

  _updateUiMenu(options) {
    this._clearMenuOptions(this.uiMenu)
    this._createMenuOptions(this.uiMenu, options)
    this._updateUiMenuHeight()
    this._hideTextFieldOnEmptyList(options)
  }

  _updateUiMenuHeight() {
    const { height } = this.suggestionsMenuWrapper.getBoundingClientRect()
    this.uiMenu.style.top = height + 6 + 'px'
  }

  _hideTextFieldOnEmptyList(options) {
    if (options.length === 0) {
      this.uiMenu.style.display = 'none'
      this.searchTextInput.style.cursor = 'default'
      return
    }
    this.uiMenu.style.display = 'block'
    this.searchTextInput.style.cursor = 'text'
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-base/lui-suggestions-menu-mixin.js
// module id = 12
// module chunks = 0 1 4 8 20